import { useEffect, useMemo, useState } from "react";
import Chessboard from "../Chessboard";
import { Chess } from "chess.js";
import { PlayerColor, Position } from "../models";
import useSound from "use-sound";
import moveSelfSound from "./../assets/sound-effects/move-self.mp3";
import { getLichessOpeningData } from "../getOpeningBookMove";
// import { fetchPositions } from "../fetchPositions";
import _ from "lodash";
import { ExplorerStorage, useExplorerStorage, useStorage } from "../ZustandState";
import { useQuery } from "react-query";
import { getUser, redirectToPremiumIfNotPremium } from "../Authentication";
import { INITIAL_POSITION } from "../utils";
import MoveStatistics2 from "../exclusions/MoveStatistics2";
import axios from "axios";
import { OtAttempt2 } from "../PositionStats";
import Header from "../Header";
import { ActionIcon, Button, List, Loader, LoadingOverlay, Popover, Text, Tooltip } from "@mantine/core";
import {
    IconAdjustments,
    IconArrowLeft,
    IconCaretLeft,
    IconChevronLeft,
    IconChevronRight,
    IconFilter,
    IconInfoCircle,
    IconRepeat,
    IconSearch,
} from "@tabler/icons-react";
import useKeypress from "react-use-keypress";
import { Pgn } from "cm-pgn/src/Pgn.js";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import ExplorerFiltersModal from "./ExplorerFiltersModal";
import { fetchLichessLink } from "../fetchLichessLink";

interface ChessMove {
    color: PlayerColor;
    commentAfter?: string;
    fen: string;
    from: string;
    next: ChessMove | null;
    piece: string;
    ply: number;
    previous: ChessMove | null;
    san: string;
    to: string;
    uci: string;
}

interface ExtendedOtAttempt2 extends OtAttempt2 {
    chessMoves: ChessMove[];
}

export const fetchAllAttempts = async (): Promise<ExtendedOtAttempt2[]> => {
    const response = await axios.get(`${import.meta.env.VITE_OPENING_TRAINER_BACKEND_URL}/ot_all_attempts`, {
        withCredentials: true,
    });

    const data = response.data;

    const extendedData = data.map((attempt: OtAttempt2) => {
        let chess;
        try {
            chess = new Pgn(attempt.pgn, { sloppy: true });
        } catch (error) {
            return undefined;
        }
        return {
            ...attempt,
            chessMoves: chess.history.moves,
        };
    });

    return _.compact(extendedData);
};

type OpeningBook = Record<string, Position>;

interface Position {
    white: number;
    draws: number;
    black: number;
    moves: Move[];
    recentGames: RecentGame[];
    topGames: any[];
    opening: Opening;
}

interface Opening {
    eco: string;
    name: string;
}

interface RecentGame {
    uci: string;
    id: string;
    winner: string;
    speed: string;
    mode: string;
    black: Black;
    white: Black;
    year: number;
    month: string;
}

interface Black {
    name: string;
    rating: number;
}

interface Move {
    uci: string;
    san: string;
    averageRating: number;
    white: number;
    draws: number;
    black: number;
    game: null;
    opening: null;
}

const getOpeningBook = (
    allAttempts: ExtendedOtAttempt2[],
    playerColor: PlayerColor,
    explorerStorage: ExplorerStorage
): OpeningBook => {
    const attemptsForColor = allAttempts.filter((attempt) => attempt.playerColor === playerColor);
    const openingBook: OpeningBook = {};

    attemptsForColor.forEach((attempt) => {
        if (!attempt.pgn) {
            return;
        }

        if (!explorerStorage.includeTrainingModeGames && !attempt.isRated) {
            return;
        }

        if (explorerStorage.fromDate && new Date(attempt.createdAt) < new Date(explorerStorage.fromDate)) {
            return;
        }

        if (explorerStorage.toDate && new Date(attempt.createdAt) > new Date(explorerStorage.toDate)) {
            return;
        }

        if (
            parseInt(attempt.positionRating as string) < explorerStorage.fromRating ||
            parseInt(attempt.positionRating as string) > explorerStorage.toRating
        ) {
            return;
        }

        // Exclude those attempts that started in middle of a game
        if (attempt.pgn.includes("SetUp")) {
            return;
        }

        for (let i = 0; i < attempt.chessMoves.length; i++) {
            const move = attempt.chessMoves[i];
            const fen = move.previous ? move.previous.fen : "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";
            const shortenedFen = fen.split(" ").slice(0, 3).join(" ");

            if (!openingBook[shortenedFen]) {
                openingBook[shortenedFen] = {
                    white: 0,
                    draws: 0,
                    black: 0,
                    moves: [],
                    recentGames: [],
                    topGames: [],
                    opening: {
                        eco: "",
                        name: "",
                    },
                };
            }
            const evaluation = parseFloat(attempt.evaluation);
            if (evaluation >= 0.6) {
                openingBook[shortenedFen].white += 1;
            } else if (evaluation > -0.1 && evaluation < 0.6) {
                openingBook[shortenedFen].draws += 1;
            } else {
                openingBook[shortenedFen].black += 1;
            }

            if (!openingBook[shortenedFen].moves.map((m) => m.san).includes(move.san)) {
                // If the move does not exist, add it to the moves array
                openingBook[shortenedFen].moves.push({
                    uci: move.uci,
                    san: move.san,
                    averageRating: 0,
                    white: 0,
                    draws: 0,
                    black: 0,
                    game: null,
                    opening: null,
                });
            }
            const existingMove = openingBook[shortenedFen].moves.find((m) => m.san === move.san)!;
            if (evaluation >= 0.6) {
                existingMove.white += 1;
            }
            if (evaluation > -0.1 && evaluation < 0.6) {
                existingMove.draws += 1;
            }
            if (evaluation <= -0.1) {
                existingMove.black += 1;
            }
        }
    });

    return openingBook;
};

const Explorer = () => {
    const [game, setGame] = useState<Chess>();
    const [selectedPosition, setSelectedPosition] = useState<Position>();
    const [playMoveSelfSound] = useSound(moveSelfSound);
    const [openedFiltersModal, { open: openFiltersModal, close: closeFiltersModal }] = useDisclosure(false);
    const explorerStorage = useExplorerStorage((state) => state);
    // const { data: positions } = useQuery(["positions"], () => fetchPositions());

    const playerColor = useStorage((state) => state.playerColor);
    const setPlayerColor = useStorage((state) => state.setPlayerColor);
    const [redoStack, setRedoStack] = useState<string[]>([]);
    // const [openedAddPositionModal, { open: openAddPositionModal, close: closeAddPositionModal }] = useDisclosure(false);

    const { data: user, isLoading: isLoadingUser } = useQuery(["user"], () => getUser());
    const { data: allAttempts, isLoading: isLoadingAttempts } = useQuery(["allAttempts"], () => fetchAllAttempts());

    const isMobile = useMediaQuery("(max-width: 1024px)");

    redirectToPremiumIfNotPremium(user);

    const openingBook = useMemo(() => {
        if (!allAttempts) {
            return {};
        }

        const timeBefore = performance.now();
        const book = getOpeningBook(allAttempts, playerColor, explorerStorage);
        const timeAfter = performance.now();
        console.log("Time taken to generate opening book:", timeAfter - timeBefore, "ms");

        return book;
    }, [playerColor, allAttempts, explorerStorage]);

    useEffect(() => {
        // if (!positions) {
        //     return;
        // }

        setSelectedPosition(INITIAL_POSITION);

        const game = new Chess(INITIAL_POSITION.fen);
        setGame(game);
        if (game.turn() === "b") {
            setPlayerColor("b");
        }
    }, []);

    useKeypress("f", () => {
        setPlayerColor(playerColor === "w" ? "b" : "w");
    });

    const undoMove = () => {
        const lastMove = _.last(game?.history());
        if (lastMove) {
            setRedoStack([...redoStack, lastMove]);
            game?.undo();
            handleOnMove(true);
        }
    };

    const redoMove = () => {
        const lastMove = _.last(redoStack);
        if (lastMove) {
            game?.move(lastMove);
            setRedoStack(redoStack.slice(0, redoStack.length - 1));
            handleOnMove(true);
        }
    };

    useKeypress(["ArrowLeft", "ArrowRight"], (event: any) => {
        if (event.key === "ArrowLeft") {
            undoMove();
        } else {
            redoMove();
        }
    });

    // if (!selectedPosition) {
    //     return;
    // }

    const handleOnMove = async (didUndoOrRedo?: boolean) => {
        if (!game) {
            return;
        }

        playMoveSelfSound();

        if (!didUndoOrRedo) {
            setRedoStack([]);
        }

        const openingResponse = await getLichessOpeningData(game);

        const lastMove = _.last(game.history());
        const moveNumber = game.history().length;
        const moveText = lastMove
            ? `${Math.ceil(moveNumber / 2)}${moveNumber % 2 === 0 ? "..." : "."} ${lastMove}`
            : "";
        const openingName =
            openingResponse.opening?.name ?? `${selectedPosition.originalName || selectedPosition.name} ${moveText}`;

        setSelectedPosition({
            name: openingName,
            fen: game.fen(),
            eco: openingResponse.opening?.eco,
            originalName: openingResponse.opening?.name ?? (selectedPosition.originalName || selectedPosition.name),
        });
        return;
    };

    const handleSetSelectedPosition = (position: Position) => {
        if (!position.fen) {
            throw new Error("Fen for position not found");
        }

        if (position.pgn) {
            game?.loadPgn(position.pgn);
        } else {
            game?.load(position.fen);
        }
        setSelectedPosition(position);
    };

    if (isLoadingUser || isLoadingAttempts || !openingBook || !game) {
        return (
            <div>
                <div className="flex items-center justify-center h-screen space-x-4">
                    <Loader size="xl" />
                    <div className="ml-4 text-lg font-bold ">Generating opening book...</div>
                </div>
            </div>
        );
        return <LoadingOverlay visible={true} />;
    }

    // 3 first parts of fen
    const shortenedFen = game.fen().split(" ").slice(0, 3).join(" ");

    const currentOpeningData = openingBook[shortenedFen] || {
        white: 0,
        draws: 0,
        black: 0,
        moves: [],
        recentGames: [],
        topGames: [],
        opening: {
            eco: "",
            name: "",
        },
    };

    const handleOnAnalyseOnLichess = async () => {
        const gameLink = await fetchLichessLink(game, playerColor, undefined, isMobile);

        // @ts-expect-error err
        window.open(gameLink).focus();
    };

    return (
        <div>
            <ExplorerFiltersModal opened={openedFiltersModal} close={closeFiltersModal} />
            <Header user={user} selected="statistics" />
            <div className="flex justify-center space-x-6 [@media(max-width:1023px)]:flex-col [@media(max-width:1023px)]:space-y-2 [@media(max-width:1023px)]:items-center">
                <Chessboard
                    game={game}
                    onMove={handleOnMove}
                    playerColor={playerColor}
                    result={undefined}
                    state="positions"
                />
                <div className="w-[400px] h-[80vh] bg-[#101012] rounded-lg border-[#3c3c3c] border-[1px] relative ">
                    <div className="ml-2 mt-2 mb-4">
                        <Text fw="bold" className="text-start">
                            {playerColor === "w" ? "White openings" : "Black openings"}
                        </Text>
                    </div>
                    <MoveStatistics2 game={game} onMove={handleOnMove} isPlayersTurn openingData={currentOpeningData} />

                    <div className="absolute bottom-2 w-full">
                        <div className="flex justify-between m-3 items-center">
                            <div>
                                <Tooltip label="Analyze on Lichess">
                                    <ActionIcon
                                        onClick={() => handleOnAnalyseOnLichess()}
                                        variant="default"
                                        w={48}
                                        h={40}
                                    >
                                        <IconSearch />
                                    </ActionIcon>
                                </Tooltip>
                                <Tooltip label="Filters">
                                    <ActionIcon
                                        onClick={() => openFiltersModal()}
                                        variant="default"
                                        w={48}
                                        h={40}
                                        ml="sm"
                                    >
                                        <IconAdjustments />
                                    </ActionIcon>
                                </Tooltip>
                                <Tooltip label="Flip board">
                                    <ActionIcon
                                        onClick={() => setPlayerColor(playerColor === "w" ? "b" : "w")}
                                        variant="default"
                                        w={48}
                                        h={40}
                                        ml="sm"
                                    >
                                        <IconRepeat />
                                    </ActionIcon>
                                </Tooltip>
                                <ActionIcon onClick={undoMove} variant="transparent" w={32} h={40} ml="md">
                                    <IconChevronLeft color="white" />
                                </ActionIcon>
                                <ActionIcon onClick={redoMove} variant="transparent" w={32} h={40}>
                                    <IconChevronRight color="white" />
                                </ActionIcon>
                            </div>
                            <Popover width={350} position="bottom" withArrow shadow="md">
                                <Popover.Target>
                                    <ActionIcon variant="transparent" className="ml-4">
                                        <IconInfoCircle />
                                    </ActionIcon>
                                </Popover.Target>
                                <Popover.Dropdown>
                                    <Text>
                                        The explorer uses all games you have played on OpeningTrainer.
                                        <br />
                                        The percentages in the bars are counted using the following:
                                    </Text>
                                    {true && (
                                        <List listStyleType="circle">
                                            <List.Item>White win: Eval above 0.6</List.Item>
                                            <List.Item>Draw: Eval between -0.1 and 0.6</List.Item>
                                            <List.Item>Black win: Eval below -0.1</List.Item>
                                        </List>
                                    )}
                                    {false && (
                                        <List listStyleType="circle">
                                            <List.Item>Advantageous: Eval below -0.1</List.Item>
                                            <List.Item>Equal: Eval between -0.1 and 0.6</List.Item>
                                            <List.Item>Disadvantageous: Eval above 0.6</List.Item>
                                        </List>
                                    )}
                                </Popover.Dropdown>
                            </Popover>
                        </div>
                    </div>
                </div>
                {/* <PositionsSidePanel
                    game={game}
                    selectedPosition={selectedPosition}
                    setSelectedPosition={handleSetSelectedPosition}
                    playerColor={playerColor}
                    setPlayerColor={setPlayerColor}
                    positions={positions}
                    openedAddPositionModal={openedAddPositionModal}
                    closeAddPositionModal={closeAddPositionModal}
                    openAddPositionModal={openAddPositionModal}
                /> */}
            </div>
        </div>
    );
};

export default Explorer;
